/// <amd-module name="Wiki/ViewCommon/Utils/PathHelper" />
import { startsWith } from "VSS/Core/Util/String";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { FormatConstants, PathConstants, RepoConstants, SpecialChars, TemplateConstants, UrlEscapeConstants } from "Wiki/ViewCommon/Constants";

/**
 * Normalizes the wiki page path
 * - Converts all back-slashes in the path to forward-slashes
 * - Adds "/" in the beginning of the path if not present
 * Example input: \a\b\c output: /a/b/c
 *         input: a\b\c  output: /a/b/c
 */
export function normalizePath(path: string): string {
    let normalizedPath = path.replace(/[\/\\]+/g, RepoConstants.PathSeparator);
    if (!startsWith(normalizedPath, RepoConstants.RootPath)) {
        normalizedPath = RepoConstants.RootPath + normalizedPath;
    }

    return normalizedPath;
}

/**
 * Decode spaces and hyphen in MD readable path
 * Example: input: "abc-def%2Defg" output: "abc def-efg"
 */
export function decodeHyphenAndSpacesInPath(path: string): string {
    return path.replace(UrlEscapeConstants.HyphenRegExp, SpecialChars.Space).replace(UrlEscapeConstants.HyphenEncodingRegExp, SpecialChars.Hyphen);
}

/**
 * Encodes spaces and hyphen in path
 * Example: input: "abc-def efg" output: "abc%20def-efg"
 *
 * Details
 * Since we allow special chars in the wiki page title following changes have been made
 * Past:
 * We stored " " as "-" in the git server,
 * We replaced " " as "-" while creating the links
 * Thus there were two types of page paths serverFriendly("-" instead of " ") and clientFriendly(as seen by user, " " is " ")
 * The links in wiki markdown were created by replacing " " to "-"
 * For attachments path we converted " " to "-" before storing in the server
 *
 * Present:
 * We convert :, ?, *, <, >, -, |, " chars to their html encoding before storing the server
 * Links will continue to have "-" instead of "space" to maintain backward compatibility
 * Since "-" is also allowed in page title now, while creating link, it is replaced by %2D (its html encoding)
 * Once the link is clicked we convert the path back before sending request
 * Attachment names will have %20 instead of space, and in server stored without converting space to "-"
 */
export function encodeHyphenAndSpacesInPath(pagePath: string): string {
    return pagePath
        .replace(UrlEscapeConstants.HyphenRegExp, UrlEscapeConstants.HyphenEncoding)
        .replace(UrlEscapeConstants.SpaceRegExp, SpecialChars.Hyphen);
}

/**
 * Gets the fileName from the path
 * - Return "" if the path is undefined
 * Example: input /a/b/c output: c
 *          input /a     output: a
 *          input a      output: a
 */
export function getFileName(path: string | undefined): string {
    if (!path) {
        return "";
    }

    const pathSeparatorIndex = path.lastIndexOf(RepoConstants.PathSeparator);
    if (pathSeparatorIndex >= 0) {
        return path.substring(pathSeparatorIndex + 1);
    }

    return path;
}

/**
 * Constructs and returns the server side git item path for the given page path.
 * @param pagePath - Page path. It does not contain .md as extension
 * @param mappedPath - Wiki root path which is the mapped path of the wiki.
 */
export function getGitItemPath(pagePath: string, mappedPath?: string): string {
    if (!mappedPath) {
        mappedPath = RepoConstants.RootPath;
    }

    if (!pagePath || pagePath === RepoConstants.RootPath) {
        return mappedPath;
    }

    const encodedPagePath = getEncodedPagePath(pagePath);
    return combineUrlPaths(mappedPath, encodedPagePath) + FormatConstants.MDFormat;
}

/**
 * Encodes Git illegal special characters in pagePath with corresponding character escapes
 * @param pagePath
 * Example   input: /a:b  output: /a%3Ab
 *           input: /a?b  output: /a%3Fb
 *           input: /a b  output: /a-b
 */
export function getEncodedPagePath(pagePath: string): string {
    let encodedPagePath = pagePath;
    for (let i = 0; i < PathConstants.GitIllegalSpecialCharEscapes.length; i++) {
        encodedPagePath = encodedPagePath.replace(PathConstants.GitIllegalSpecialCharsRegExp[i], PathConstants.GitIllegalSpecialCharEscapes[i]);
    }

    return encodedPagePath.replace(UrlEscapeConstants.SpaceRegExp, SpecialChars.Hyphen);
}

/**
 * Returns page link.
 * Example: input: "abc def-efg" output: "abc-def%2Defg"
 * Example: input: "abc(defgh)(" output: "abc\(def\)\("
 * @param path - PagePath
 */
export function getLinkFromPath(pagePath: string): string {
    let path = encodeHyphenAndSpacesInPath(pagePath);
    // Escape opening and closing braces in page name
    path = path
        .replace(UrlEscapeConstants.OpeningBracesRegExp, UrlEscapeConstants.OpeningBracesEncoding)
        .replace(UrlEscapeConstants.ClosingBracesRegExp, UrlEscapeConstants.ClosingBracesEncoding);

    return path;
}

/**
 * Gets the parentPath for the given path
 * - Return "" if the path is undefined
 * Example: input /a/b/c/ output: /a/b
 *          input /a/b/c  output: /a/b
 *          input a/b/c   output: a/b
 *          input abc     output: (rootPath)
 *          input /abc    output: (rootPath)
 */
export function getParentPath(path: string | undefined, mappedPath: string = RepoConstants.RootPath): string {
    if (!path) {
        return "";
    }

    if (path[path.length - 1] === RepoConstants.PathSeparator) {
        // Trim an ending slash
        path = path.substr(0, path.length - 1);
    }

    const slashIndex = path.lastIndexOf(RepoConstants.PathSeparator);
    if (slashIndex >= 0) {
        path = path.substr(0, slashIndex);
        if (!path) {
            path = mappedPath;
        }
    } else {
        path = mappedPath;
    }

    return path;
}

export function getDepthOfPage(pagePath: string): number {
    if (pagePath === RepoConstants.RootPath) {
        return 0;
    }

    return pagePath.split(RepoConstants.PathSeparator).length - 1;
}

export function isInclude(path: string): boolean {
    return startsWith(path, TemplateConstants.TemplateDirectory);
}

export function normalizeWikiPagePath(pagePath: string | undefined): string | undefined {
    if (!pagePath) {
        return pagePath;
    }

    /* '-' in URL relates to space in the page path.
     * '%2D' in URL relates to '-' in page path.
     */
    let normalizedPagePath = normalizePath(pagePath);
    normalizedPagePath = decodeHyphenAndSpacesInPath(normalizedPagePath);
    return normalizedPagePath;
}

export function removeMdExtensionFromPagePath(path: string): string {
    const extensionStart = path.lastIndexOf(".");

    return FormatConstants.MDFormat.toLocaleUpperCase().localeCompare(path.substring(extensionStart).toLocaleUpperCase(), navigator.language) === 0
        ? path.substring(0, extensionStart)
        : path;
}

/**
 * Encodes Git illegal special characters in pagePath with corresponding character escapes
 * @param pagePath
 * Example   input: /Folder1/a:b  output: /a-b
 *           input: /Folder1/a?:b  output: /a-b
 *           input: /Folder1/a b  output: /a-b
 */
export function getFriendlyPageTitle(pagePath: string): string {
    if (!pagePath) {
        return "";
    }

    let friendlyTitle = getFileName(pagePath);
    const unFriendlyCharactersRegex = /[ `\-@$%^&+={}|\[\]:";<>?,*]/g;
    friendlyTitle = friendlyTitle.replace(unFriendlyCharactersRegex, SpecialChars.Hyphen);
    return friendlyTitle.replace(UrlEscapeConstants.ContiguousHyphenRegExp, SpecialChars.Hyphen);
}
